/* eslint-disable jsx-a11y/no-static-element-interactions */
/* eslint-disable jsx-a11y/click-events-have-key-events */
import React from 'react';
import { Trans } from 'react-i18next';
import {
  Genre,
  Momentum,
  Peakairplay,
  Region,
  Rotation,
} from 'src/assets/customIcons';
import { useSelector } from 'react-redux';
import { RootState } from 'src/app/store';
import { trackEvent } from 'src/utils/segmentService';
import { CLICK_EVENT } from 'src/consts/analytics_consts';
import {
  InsightsContainerMobile,
  InsightsCardMobile,
  InsightsWrapperMobile,
  CardTypeWrapper,
  CardType,
  CardStatus,
  OverviewWrapper,
  OverviewInsightsStyle,
} from './SmartInsights.styles';
import { SongLibraryInsights } from '../Library';
import OverviewInsightsSkeleton from './OverviewInsightsSkeleton';
import { renderGenreTransContent } from './renderGenreTransContent';
import { renderRegionTransContent } from './renderRegionTransContent';

interface OverviewInsightsMobileProps {
  isLoading?: boolean;
  insightCounts: SongLibraryInsights;
  topRegions?: string[];
  topGenres?: string[];
  onInsightClick: (insightType: string, detail?: string) => void;
  selectedInsightType?: string;
}

const OverviewInsightsMobile: React.FC<OverviewInsightsMobileProps> = ({
  isLoading,
  insightCounts,
  topRegions = [],
  topGenres = [],
  onInsightClick,
  selectedInsightType,
}) => {
  const { songInsights } = useSelector((state: RootState) => state.dashboard);
  const showSkeleton =
    isLoading ?? songInsights?.isFetchingSongInsights ?? false;

  // Handling insight click with tracking
  const handleInsightClickWithTracking = (
    insightType: string,
    detail?: string
  ) => {
    trackEvent(CLICK_EVENT, {
      type: insightType,
      source: 'dashboard_library_page',
      ...(detail ? { insight_detail: detail } : {}),
    });
    onInsightClick(insightType, detail);
  };

  if (showSkeleton) {
    return <OverviewInsightsSkeleton variant="mobile" />;
  }

  const hasMomentumInsights =
    Number(insightCounts.momentumInsightsGain) > 0 ||
    Number(insightCounts.momentumInsightsStagnant) > 0 ||
    Number(insightCounts.momentumInsightsDown) > 0;
  const isMomentumSelected =
    selectedInsightType === 'momentum_gain' ||
    selectedInsightType === 'momentum_stagnant' ||
    selectedInsightType === 'momentum_slowing';
  const isPeakSelected =
    selectedInsightType === 'peak_morning' ||
    selectedInsightType === 'peak_evening' ||
    selectedInsightType === 'peak_night';
  const isRotationSelected = selectedInsightType === 'rotation';
  const isRegionSelected = selectedInsightType === 'region';
  const isGenreSelected = selectedInsightType === 'genre';

  return (
    <>
      {/* Overview insights - mobile (display order: Momentum, Peak Airplay, Rotation, Region, Genre) */}
      <InsightsWrapperMobile>
        {/* ---- 1. Momentum ---- */}
        {hasMomentumInsights && (
          <InsightsContainerMobile>
            <InsightsCardMobile $active={isMomentumSelected}>
              <CardTypeWrapper>
                <Momentum />
                <CardType>
                  <Trans i18nKey="MOMENTUM_LABEL" />
                </CardType>
              </CardTypeWrapper>
              <CardStatus>
                <OverviewWrapper>
                  {/* Gaining */}
                  {insightCounts.momentumInsightsGain > 0 && (
                    <OverviewInsightsStyle
                      onClick={() =>
                        handleInsightClickWithTracking('momentum_gain')
                      }
                    >
                      <Trans
                        i18nKey="SMART_INSIGHTS_MOMENTUM_GAINING"
                        values={{ count: insightCounts.momentumInsightsGain }}
                        components={{
                          highlight: (
                            <strong
                              style={{
                                textDecoration: 'underline',
                                cursor: 'pointer',
                              }}
                            />
                          ),
                        }}
                      />
                    </OverviewInsightsStyle>
                  )}
                  {/* Stagnant */}
                  {insightCounts.momentumInsightsStagnant > 0 && (
                    <OverviewInsightsStyle
                      onClick={() =>
                        handleInsightClickWithTracking('momentum_stagnant')
                      }
                    >
                      <Trans
                        i18nKey="SMART_INSIGHTS_MOMENTUM_STAGNANT"
                        values={{
                          count: insightCounts.momentumInsightsStagnant,
                        }}
                        components={{
                          highlight: (
                            <strong
                              style={{
                                textDecoration: 'underline',
                                cursor: 'pointer',
                              }}
                            />
                          ),
                        }}
                      />
                    </OverviewInsightsStyle>
                  )}
                  {/* Slowing */}
                  {insightCounts.momentumInsightsDown > 0 && (
                    <OverviewInsightsStyle
                      onClick={() =>
                        handleInsightClickWithTracking('momentum_slowing')
                      }
                    >
                      <Trans
                        i18nKey="SMART_INSIGHTS_MOMENTUM_SLOWING"
                        values={{ count: insightCounts.momentumInsightsDown }}
                        components={{
                          highlight: (
                            <strong
                              style={{
                                textDecoration: 'underline',
                                cursor: 'pointer',
                              }}
                            />
                          ),
                        }}
                      />
                    </OverviewInsightsStyle>
                  )}
                </OverviewWrapper>
              </CardStatus>
            </InsightsCardMobile>
          </InsightsContainerMobile>
        )}

        {/* ---- 2. Peak Airplay ---- */}
        {insightCounts.peakAirplayMorningInsights > 0 && (
          <InsightsContainerMobile>
            <InsightsCardMobile $active={isPeakSelected}>
              <CardTypeWrapper>
                <Peakairplay />
                <CardType>
                  <Trans i18nKey="PEAK_AIRPLAY_LABEL" />
                </CardType>
              </CardTypeWrapper>
              <CardStatus>
                <OverviewWrapper>
                  {/* Morning */}
                  {insightCounts.peakAirplayMorningInsights > 0 && (
                    <OverviewInsightsStyle
                      onClick={() =>
                        handleInsightClickWithTracking('peak_morning')
                      }
                    >
                      <Trans
                        i18nKey="SMART_INSIGHTS_PEAK_MORNING"
                        values={{
                          count: insightCounts.peakAirplayMorningInsights,
                        }}
                        components={{
                          highlight: (
                            <strong
                              style={{
                                textDecoration: 'underline',
                                cursor: 'pointer',
                              }}
                            />
                          ),
                        }}
                      />
                    </OverviewInsightsStyle>
                  )}
                  {/* Evening */}
                  {insightCounts.peakAirplayEveningInsights > 0 && (
                    <OverviewInsightsStyle
                      onClick={() =>
                        handleInsightClickWithTracking('peak_evening')
                      }
                    >
                      <Trans
                        i18nKey="SMART_INSIGHTS_PEAK_EVENING"
                        values={{
                          count: insightCounts.peakAirplayEveningInsights,
                        }}
                        components={{
                          highlight: (
                            <strong
                              style={{
                                textDecoration: 'underline',
                                cursor: 'pointer',
                              }}
                            />
                          ),
                        }}
                      />
                    </OverviewInsightsStyle>
                  )}
                  {/* Night */}
                  {insightCounts.peakAirplayNightInsights > 0 && (
                    <OverviewInsightsStyle
                      onClick={() =>
                        handleInsightClickWithTracking('peak_night')
                      }
                    >
                      <Trans
                        i18nKey="SMART_INSIGHTS_PEAK_NIGHT"
                        values={{
                          count: insightCounts.peakAirplayNightInsights,
                        }}
                        components={{
                          highlight: (
                            <strong
                              style={{
                                textDecoration: 'underline',
                                cursor: 'pointer',
                              }}
                            />
                          ),
                        }}
                      />
                    </OverviewInsightsStyle>
                  )}
                </OverviewWrapper>
              </CardStatus>
            </InsightsCardMobile>
          </InsightsContainerMobile>
        )}

        {/* ---- 3. Rotation ---- */}
        {insightCounts.rotationInsights > 0 && (
          <InsightsContainerMobile>
            <InsightsCardMobile $active={isRotationSelected}>
              <CardTypeWrapper>
                <Rotation />
                <CardType>
                  <Trans i18nKey="ROTATION_LABEL" />
                </CardType>
              </CardTypeWrapper>
              <CardStatus>
                <OverviewWrapper>
                  <OverviewInsightsStyle
                    onClick={() => handleInsightClickWithTracking('rotation')}
                  >
                    <Trans
                      i18nKey="SMART_INSIGHTS_ROTATION_ADDED"
                      values={{ count: insightCounts.rotationInsights }}
                      components={{
                        highlight: (
                          <strong
                            style={{
                              textDecoration: 'underline',
                              cursor: 'pointer',
                            }}
                          />
                        ),
                      }}
                    />
                  </OverviewInsightsStyle>
                </OverviewWrapper>
              </CardStatus>
            </InsightsCardMobile>
          </InsightsContainerMobile>
        )}

        {/* ---- 4. Region ---- */}
        {insightCounts.regionsInsights > 0 && (
          <InsightsContainerMobile>
            <InsightsCardMobile $active={isRegionSelected}>
              <CardTypeWrapper>
                <Region />
                <CardType>
                  <Trans i18nKey="REGION_LABEL" />
                </CardType>
              </CardTypeWrapper>
              <CardStatus>
                <OverviewWrapper>
                  {/* Top region */}
                  <OverviewInsightsStyle
                    onClick={() =>
                      handleInsightClickWithTracking('region', topRegions[0])
                    }
                  >
                    {renderRegionTransContent(
                      topRegions,
                      handleInsightClickWithTracking
                    )}
                  </OverviewInsightsStyle>
                </OverviewWrapper>
              </CardStatus>
            </InsightsCardMobile>
          </InsightsContainerMobile>
        )}

        {/* ---- 5. Genre ---- */}
        {insightCounts.genresInsights > 0 && (
          <InsightsContainerMobile>
            <InsightsCardMobile $active={isGenreSelected}>
              <CardTypeWrapper>
                <Genre />
                <CardType>
                  <Trans i18nKey="GENRE_LABEL" />
                </CardType>
              </CardTypeWrapper>
              <CardStatus>
                <OverviewWrapper>
                  {/* Top genre */}
                  <OverviewInsightsStyle
                    onClick={() =>
                      handleInsightClickWithTracking('genre', topGenres[0])
                    }
                  >
                    {renderGenreTransContent(
                      topGenres,
                      handleInsightClickWithTracking
                    )}
                  </OverviewInsightsStyle>
                </OverviewWrapper>
              </CardStatus>
            </InsightsCardMobile>
          </InsightsContainerMobile>
        )}
      </InsightsWrapperMobile>
    </>
  );
};

export default OverviewInsightsMobile;
